import {
  FontIcon,
  FontIcons,
  FontSizes,
  FontWeights,
  HexColorPicker,
  Placements,
  StandardHeading,
  useFetch,
  UseFetchOptions
} from '@brandfolder/react';
import classNames from 'classnames';
import { FunctionComponent, HTMLAttributes, useContext, useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import styled from 'styled-components';

import { BrandguideContext } from '@context/brandguide';
import { ThemeContext } from '@context/theme';
import { ApiDatum } from '@typings/api';
import { Theme, ThemeBody } from '@typings/theme';

type BodyType = keyof ThemeBody;

interface SettingsColorProps extends HTMLAttributes<HTMLDivElement> {
  id: string;
  textfieldLabel: string;
  type: BodyType;
  showGlobalHeading?: boolean;
}

const UnstyledSettingsColor: FunctionComponent<SettingsColorProps> = (props) => {
  const { id, showGlobalHeading, textfieldLabel, type, ...otherProps } = props;

  const { brandguide } = useContext(BrandguideContext);
  const { setTheme, theme } = useContext(ThemeContext);

  const ref = useRef('');
  const [localColor, setLocalColor] = useState(theme.body[type]);
  const [mounted, setMounted] = useState(false);

  const getThemeBody = (): ThemeBody => {
    return {
      ...theme.body,
      [`${type}`]: localColor
    };
  };

  const options: UseFetchOptions = {
    body: {
      data: {
        attributes: { body: getThemeBody() }
      }
    },
    fetchOnMount: false,
    headers: {
      'Content-Type': 'application/json',
      'x-brandguide-key': brandguide?.key || ''
    },
    url: `/api/themes/${theme.key}`,
    method: 'PATCH'
  };

  const { error, fetch, loading, response } = useFetch<ApiDatum<Partial<Theme>>>(options);

  useEffect(() => {
    if (loading) {
      ref.current = toast.loading('Saving...');
    }
  }, [loading]);

  useEffect(() => {
    if (response) {
      setTheme({
        ...theme,
        body: {
          ...theme.body,
          [`${type}`]: (response.data.attributes.body as ThemeBody)[`${type}`]
        }
      });
      toast.dismiss(ref.current);
      toast.success('Saved!');
    }
  }, [response]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (error) {
      toast.dismiss(ref.current);
      toast.error('Error saving.');
    }
  }, [error]);

  useEffect(() => {
    if (mounted) {
      fetch();
    }
  }, [localColor]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setMounted(true);

    return () => {
      ref.current = '';
    };
  }, []);

  return (
    <div
      {...otherProps}
      className={classNames({
        'global-settings__wrapper': true,
        [`global-settings__wrapper--${type}`]: type,
        [`${otherProps.className}`]: !!otherProps.className
      })}
    >
      <div className="background-hex-color">
        {showGlobalHeading && (
          <div className="global-wrapper">
            <FontIcon className="language" icon={FontIcons.Language} iconSize={20} />
            <StandardHeading
              className="global-text"
              size={FontSizes.XSmall}
              weight={FontWeights.Medium}
            >
              Global
            </StandardHeading>
          </div>
        )}
        <HexColorPicker
          defaultColor={localColor}
          id={id}
          insidePopout
          labels={{
            buttonLabel: 'Open color picker',
            closeButtonLabel: 'Close',
            requiredLabel: 'Required',
            textfieldLabel
          }}
          onChange={(color) => {
            setLocalColor(color);
          }}
          popoutProps={{
            placement: Placements.BottomCenter
          }}
          textfieldProps={{
            className: 'text-field'
          }}
        />
      </div>
    </div>
  );
};

const StyledSettingsColor = styled(UnstyledSettingsColor)`
  .global-wrapper {
    align-items: center;
    display: flex;
    margin-bottom: 10px;

    .language {
      margin-right: 15px;
    }

    .global-text {
      font-weight: 500;
      letter-spacing: 0.5px;
      text-transform: uppercase;
    }
  }

  .bf-textfield {
    text-transform: uppercase;
  }

  .bf-textfield__hex {
    .bf-popout__header {
      border-bottom: 0;
      padding: 0;
    }
  }

  &.global-settings__wrapper--color {
    .bf-textfield__hex {
      margin-top: 24px;
    }
  }

  #background-color-popout-popout {
    z-index: 4;
  }

  #text-color-popout-popout {
    z-index: 2;
  }
`;

export const SettingsColor: FunctionComponent<SettingsColorProps> = (props) => {
  return <StyledSettingsColor {...props} />;
};
